import {HttpClient} from "@angular/common/http";
import {Injectable} from "@angular/core";
import {BehaviorSubject, map, Observable, tap} from "rxjs";
import {
  ActionResult,
  ActionResultList,
  League,
  Lookups,
  PagedFilter,
  Request,
  RequestFilter,
  RequestStatus,
  RequestTypes,
  ValidatorResult
} from "@model";
import {LoaderService} from "./loader.service";
import {EnvironmentService} from "./environment.service";

@Injectable({ providedIn: 'root' })
export class RequestService {
  public static EXISTING_ORGANISER_REQUEST_ON_HOLD = 1;

  private _fetchNewRequests = new BehaviorSubject<boolean | null>(null);

  get fetchNewRequests() { return this._fetchNewRequests; }

  constructor(
    private http: HttpClient,
    private loaderService: LoaderService,
    private environmentService: EnvironmentService
  ) {}

  private handleLoading<T>(observable: Observable<T>, hideLoading?: boolean): Observable<T> {
    if (!hideLoading) this.loaderService.startLoading();
    return observable.pipe(tap(() => !hideLoading && this.loaderService.dismissLoading()));
  }

  sendPublishEventRequest(request: Request, onSuccess: () => void, onError: () => void): Observable<ValidatorResult | null> {
    return this.handleLoading(
      this.http.post<ActionResult<ValidatorResult>>(this.environmentService.getApiUrl() + "/api/requests/event/new", request)
    ).pipe(map(value => {
      const result = value?.result ? ValidatorResult.deserialize(value.result) : null;
      if (result?.hasErrors()) return result;
      result ? onSuccess() : onError();
      return null;
    }));
  }

  sendPublishLeagueRequest(request: Request, onSuccess: () => void, onError: () => void): Observable<ValidatorResult | null> {
    return this.handleLoading(
      this.http.post<ActionResult<ValidatorResult>>(this.environmentService.getApiUrl() + "/api/requests/league/new", request)
    ).pipe(map(value => {
      const result = value?.result ? ValidatorResult.deserialize(value.result) : null;
      if (result?.hasErrors()) return result;
      result ? onSuccess() : onError();
      return null;
    }));
  }

  manageEventRequest(request: Request): Observable<ValidatorResult | undefined> {
    return this.handleLoading(
      this.http.post<ActionResult<ValidatorResult>>(this.environmentService.getApiUrl() + "/api/requests/manage/event", request)
    ).pipe(map(value => value?.result ? ValidatorResult.deserialize(value.result) : undefined));
  }

  sendOrganiserRequest(request: Request, onSuccess: () => void, onError: (errorCode?: number) => void) {
    this.handleLoading(
      this.http.post<ActionResult<boolean>>(this.environmentService.getApiUrl() + "/api/requests/organiser/new", request)
    ).subscribe(actionResult => (actionResult.resultCode !== -1 && actionResult.resultCode !== 1) ? onSuccess() : onError(actionResult.resultCode));
  }

  manageRequest(url: string, request: Request): Observable<boolean> {
    return this.handleLoading(
      this.http.post<ActionResult<boolean>>(this.environmentService.getApiUrl() + url, request)
    ).pipe(map(value => value.result ?? false));
  }

  manageOrganiserRequest(request: Request): Observable<boolean> {
    return this.manageRequest("/api/requests/manage/organiser", request);
  }

  managePublishLeagueRequest(request: Request): Observable<boolean> {
    return this.manageRequest("/api/requests/manage/league/publish", request);
  }

  managePublishLeagueRequestByLeague(league: League, accept: boolean, comment: string | undefined): Observable<boolean> {
    var request: Request = new Request();
    request.league = league;
    request.requestStatus = accept ? RequestStatus.ACCEPTED : RequestStatus.DENIED;
    request.comment = comment;
    request.type = RequestTypes.PUBLISH_LEAGUE;
    return this.manageRequest("/api/requests/manage/league/publish", request);
  }

  manageAddEventToLeagueRequest(request: Request): Observable<boolean> {
    return this.manageRequest("/api/requests/manage/league/event", request);
  }

  filterRequests(
      requestFilter: PagedFilter<RequestFilter>,
      incoming: boolean = true
  ): Observable<Request[]> {

    const url = this.environmentService.getApiUrl() +
        (incoming ? "/api/requests/incoming" : "/api/requests/outgoing");

    return this.http.post<ActionResultList<Request[], number>>(url, requestFilter).pipe(
        map(value => {
          return value?.result?.length ? value.result : []
        })
    );
  }

  fetchRequestsCount(requestFilter: RequestFilter): Observable<number> {
    return this.http.post<ActionResult<number>>(this.environmentService.getApiUrl() + "/api/requests/count", requestFilter).pipe(
      map(value => value.result ?? 0)
    )
  }

  fetchRequestById(requestId: number | undefined, hideLoading?: boolean): Observable<Request | null> {
    return this.handleLoading(
      this.http.post<ActionResult<Request>>(this.environmentService.getApiUrl() + "/api/requests/fetchbyid", requestId),
      hideLoading
    ).pipe(map(value => {
      return value?.result ?? null;
    }));
  }

  filterRequestTypes(incoming: boolean = true) : Observable<Lookups[]> {

    const url = this.environmentService.getApiUrl() +
        (incoming ? "/api/public/lookups/requesttypes/filter/incoming" : "/api/public/lookups/requesttypes/filter/outgoing");

    return this.http.get<ActionResultList<Lookups[], number>>(url).pipe(
        map(value => {
          return value?.result?.length ? value.result : []
        })
    );
  }
}
